import { ErrorMessage, LoadingOverlay } from '@hex/calculators-shared/components';
import { ResultWhiteBox } from '@hex/calculators-shared/layout';
import { InterestRatesResponse } from '@hex/calculators-shared/types';
import { track } from '@hex/shared/tracking';
import { Button, Flex, tokens, View, ToggleButton, Typography } from '@sapphire-react/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BankLogo } from './BankLogo';

type Props = {
  data: InterestRatesResponse | null;
  isLoading: boolean;
  hasError: boolean;
};

export function Results({ data, isLoading, hasError }: Props) {
  const [selection, setSelection] = useState(0);
  const { t } = useTranslation();
  const rates = data?.interestRates;
  const disabled = !rates;

  const interestRates = [
    { value: rates?.ThreeMonths, text: '3 mån' },
    { value: rates?.OneYear, text: '1 år' },
    { value: rates?.TwoYears, text: '2 år' },
    { value: rates?.ThreeYears, text: '3 år' },
    { value: rates?.FourYears, text: '4 år' },
    { value: rates?.FiveYears, text: '5 år' },
    { value: rates?.SixYears, text: '6 år' },
    { value: rates?.TenYears, text: '10 år' },
  ];

  if (hasError) {
    return <ErrorMessage />;
  }

  const selectionValue = interestRates[selection].value;
  const interestRate = selectionValue
    ? new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2 }).format(selectionValue)
    : '-';

  return (
    <LoadingOverlay isLoading={isLoading}>
      <ResultWhiteBox disabled={disabled}>
        <Header />
        <div aria-hidden>
          <Typography.Heading
            level={1}
            fontWeight={'bold'}
            marginTop={tokens.size.spacingMd}
            data-test-id={'result-interest-rate'}
          >
            {`${interestRate} %`}
          </Typography.Heading>
        </div>
        <InterestRatesBubbles
          interestRates={interestRates}
          selection={selection}
          setSelection={setSelection}
          disabled={disabled}
        />
        <Button
          data-test-id={'Navigate-Loan-Application-Button'}
          marginTop={tokens.size.spacing2xl}
          iconAlign={'right'}
          isDisabled={disabled}
          onPress={() => {
            track('lead:housing:rate-calculator');
            window.open('https://danskebank.se/bostad/ansok-om-bolan/', '_blank', 'noopener,noreferrer');
          }}
          icon={
            <S.IconContainer>
              <BankLogo />
            </S.IconContainer>
          }
        >
          {t('applyForLoan')}
        </Button>
        <Typography.Body
          marginTop={tokens.size.spacingMd}
          UNSAFE_style={{ color: tokens.color.foregroundSecondary.value }}
          size={'sm'}
          textAlign={'center'}
        >
          {t('calculationResult.note')}
        </Typography.Body>
      </ResultWhiteBox>
    </LoadingOverlay>
  );
}

function Header() {
  const { t } = useTranslation();

  return (
    <>
      <Typography.Heading level={4}>{t('calculationResult.heading')}</Typography.Heading>
      <View marginTop={tokens.size.spacing3xs}>
        <Typography.Body UNSAFE_style={{ color: tokens.color.foregroundSecondary.value }} size={'sm'}>
          {t('calculationResult.description')}
        </Typography.Body>
      </View>
    </>
  );
}

function InterestRatesBubbles({
  interestRates,
  selection,
  setSelection,
  disabled,
}: {
  interestRates: {
    value?: number;
    text: string;
  }[];
  selection: number;
  setSelection: (index: number) => void;
  disabled?: boolean;
}) {
  return (
    <Flex
      justifyContent={'center'}
      marginTop={tokens.size.spacingSm}
      flexWrap={'wrap'}
      gap={8}
      aria-label={'Här är en lista med åtta bolåneräntor med olika bindningstider'}
      aria-hidden={disabled}
    >
      {interestRates.map((option, index) => {
        const isSelected = selection === index;

        return (
          <ToggleButton
            aria-label={`${option.text} - ${option.value} %`}
            key={index}
            size={'sm'}
            isSelected={isSelected}
            onChange={() => setSelection(index)}
            isDisabled={!option.value}
            data-test-id={`interest-rate-bubbles-${index}-Option`}
            UNSAFE_style={{
              backgroundColor: isSelected ? tokens.color.backgroundActionSelectDefault.value : undefined,
              color: isSelected ? tokens.color.foregroundActionOnSelectDefault.value : undefined,
            }}
          >
            {option.text}
          </ToggleButton>
        );
      })}
    </Flex>
  );
}

const S = {
  IconContainer: styled.div`
    box-sizing: border-box;
    padding: 1px;
  `,
};
